import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import { useApp } from "../store";
import { derive, type DerivedSchool } from "../lib/derive";
import { TIER_LABELS, TIER_ORDER, type Tier } from "../lib/fitTier";
import { int, pct, orDash, raceTimeToSeconds, sortableName, usdK } from "../lib/format";
import { ScholarshipDollars, StudentLoveHearts, TierBadge } from "../components/badges";
import { StatusControl } from "../components/StatusControl";
import { SchoolLogo } from "../components/SchoolBrand";
import { DOLLAR_LABEL } from "../lib/scholarship";
import type { Division, FilterPreset, School, Status } from "../lib/types";

const DIVISIONS: Division[] = ["DI", "DII", "DIII"];
const PROXIMITIES = ["Close", "Regional", "Far", "Unknown"] as const;
const TIERS: Tier[] = ["dream", "stretch", "target", "floor", "unknown"];
const STATUSES: Status[] = ["unranked", "keep", "maybe", "cut"];
const DOLLARS = ["5", "4", "3", "2", "1", "0"] as const;
const LOVE = ["5", "4", "3", "2", "1", "0", "none"] as const;
const PT_OPTS = ["has_pt", "related", "none"] as const;
const PT_LABEL: Record<string, string> = {
  has_pt: "PT program",
  related: "Pre-PT majors",
  none: "neither",
};
const ptRank = (s: School) => (s.pt_program === "has_pt" ? 2 : s.pt_program === "related" ? 1 : 0);
const ptKey = (s: School) => s.pt_program ?? "none";

export default function ListPage() {
  const { schools, settings, putEvaluation, filterPresets, saveFilterPresets } = useApp();
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>([{ id: "katieRank", desc: false }]);

  const [q, setQ] = useState("");
  const [fDiv, setFDiv] = useState<Set<string>>(new Set());
  const [fProx, setFProx] = useState<Set<string>>(new Set());
  const [fTier, setFTier] = useState<Set<string>>(new Set());
  const [fStatus, setFStatus] = useState<Set<string>>(new Set());
  const [fSchol, setFSchol] = useState<Set<string>>(new Set());
  const [fLove, setFLove] = useState<Set<string>>(new Set());
  const [fPt, setFPt] = useState<Set<string>>(new Set());
  const [hideCut, setHideCut] = useState(false);
  const [dadOnly, setDadOnly] = useState(false);
  const [claudeOnly, setClaudeOnly] = useState(false);

  const [savingName, setSavingName] = useState<string | null>(null);

  const applyPreset = (p: FilterPreset) => {
    setQ(p.q ?? "");
    setFDiv(new Set(p.div));
    setFProx(new Set(p.prox));
    setFTier(new Set(p.tier));
    setFStatus(new Set(p.status));
    setFSchol(new Set(p.schol));
    setFLove(new Set(p.love));
    setFPt(new Set(p.pt));
    setDadOnly(!!p.dadOnly);
    setClaudeOnly(!!p.claudeOnly);
    setHideCut(!!p.hideCut);
  };

  const saveCurrent = () => {
    const name = (savingName ?? "").trim();
    if (!name) return;
    const p: FilterPreset = {
      name,
      q,
      div: [...fDiv],
      prox: [...fProx],
      tier: [...fTier],
      status: [...fStatus],
      schol: [...fSchol],
      love: [...fLove],
      pt: [...fPt],
      dadOnly,
      claudeOnly,
      hideCut,
    };
    const next = [...filterPresets.filter((x) => x.name !== name), p].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
    void saveFilterPresets(next);
    setSavingName(null);
  };

  const deletePreset = (name: string) => {
    void saveFilterPresets(filterPresets.filter((x) => x.name !== name));
  };

  const rows = useMemo(() => {
    if (!settings) return [];
    return schools.map((s) => derive(s, settings));
  }, [schools, settings]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((d) => {
      const s = d.school;
      if (needle && !`${s.name} ${s.conference ?? ""} ${s.state ?? ""}`.toLowerCase().includes(needle))
        return false;
      if (fDiv.size && !fDiv.has(s.division)) return false;
      if (fProx.size && !fProx.has(d.proximity)) return false;
      if (fTier.size && !fTier.has(d.tier)) return false;
      if (fStatus.size && !fStatus.has(s.evaluation.status)) return false;
      if (fSchol.size && !fSchol.has(String(s.scholarship_dollars ?? 0))) return false;
      if (fLove.size && !fLove.has(s.student_love == null ? "none" : String(s.student_love)))
        return false;
      if (fPt.size && !fPt.has(ptKey(s))) return false;
      if (dadOnly && !s.evaluation.dadPick) return false;
      if (claudeOnly && !s.claude_pick) return false;
      if (hideCut && s.evaluation.status === "cut") return false;
      return true;
    });
  }, [rows, q, fDiv, fProx, fTier, fStatus, fSchol, fLove, fPt, dadOnly, claudeOnly, hideCut]);

  const columns = useMemo<ColumnDef<DerivedSchool>[]>(
    () => [
      {
        id: "name",
        header: "School",
        accessorFn: (d) => sortableName(d.school.name),
        cell: ({ row }) => (
          <div className="flex min-w-[220px] items-center gap-2">
            <SchoolLogo school={row.original.school} size={28} />
            <div>
              <div className="font-medium text-slate-900">{row.original.school.name}</div>
              <div className="text-xs text-slate-500">
                {row.original.school.division} · {orDash(row.original.school.conference)} ·{" "}
                {orDash(row.original.school.state)}
              </div>
            </div>
          </div>
        ),
      },
      {
        id: "katieRank",
        header: "Katie rank",
        accessorFn: (d) => d.school.evaluation.katieRank ?? 9999,
        cell: ({ row }) => (
          <div onClick={(e) => e.stopPropagation()}>
            <RankCell
              value={row.original.school.evaluation.katieRank}
              onChange={(v) => void putEvaluation(row.original.school.id, { katieRank: v })}
            />
          </div>
        ),
      },
      {
        id: "proximity",
        header: "Distance",
        accessorFn: (d) => ["Close", "Regional", "Far", "Unknown"].indexOf(d.proximity),
        cell: ({ row }) => (
          <span title={row.original.school.distance_bucket ?? ""}>{row.original.proximity}</span>
        ),
      },
      {
        id: "tier",
        header: "Fit tier",
        accessorFn: (d) => TIER_ORDER[d.tier],
        cell: ({ row }) => <TierBadge tier={row.original.tier} />,
      },
      {
        id: "est2k",
        header: () => <StackHeader top="Est. 1V" bottom="8+ 2k" />,
        // Sort on real seconds, and park "no 2k data" schools past the slowest time
        // (a huge sentinel) so ascending sort lands them at the bottom, not the top.
        accessorFn: (d) => raceTimeToSeconds(d.school.est_8_2k_time) ?? Number.MAX_SAFE_INTEGER,
        cell: ({ row }) => orDash(row.original.school.est_8_2k_time),
      },
      {
        id: "scholarship",
        header: "Scholarship $",
        accessorFn: (d) => d.school.scholarship_dollars ?? 0,
        cell: ({ row }) => <ScholarshipDollars school={row.original.school} />,
      },
      {
        id: "admit",
        header: "Admit rate",
        accessorFn: (d) => d.school.admission_rate ?? 2,
        cell: ({ row }) => pct(row.original.school.admission_rate),
      },
      {
        id: "cost",
        header: "Cost",
        accessorFn: (d) => d.school.net_price ?? Number.MAX_SAFE_INTEGER,
        cell: ({ row }) => (
          <span title="Average annual net cost after aid (College Scorecard)">
            {usdK(row.original.school.net_price)}
          </span>
        ),
      },
      {
        id: "earn",
        header: () => <StackHeader top="Median grad" bottom="earnings" />,
        accessorFn: (d) => d.school.earnings_10yr ?? -1,
        cell: ({ row }) => (
          <span title="Median earnings 10 years after entry (College Scorecard)">
            {usdK(row.original.school.earnings_10yr)}
          </span>
        ),
      },
      {
        id: "love",
        header: () => (
          <>
            Student <span className="text-rose-500">♥</span>
          </>
        ),
        accessorFn: (d) => d.school.retention_vs_expected ?? -1,
        cell: ({ row }) => <StudentLoveHearts school={row.original.school} />,
      },
      {
        id: "pt",
        header: "PT",
        accessorFn: (d) => ptRank(d.school),
        cell: ({ row }) => <PtMark school={row.original.school} />,
      },
      {
        id: "claudePick",
        header: () => <StackHeader top="Claude" bottom="25" />,
        // Picks first when sorted ascending; everything else parked past #25.
        accessorFn: (d) => d.school.claude_pick?.rank ?? 999,
        cell: ({ row }) => <ClaudePickMark school={row.original.school} />,
      },
      {
        id: "dad",
        header: "Dad",
        accessorFn: (d) => (d.school.evaluation.dadPick ? 0 : 1),
        cell: ({ row }) => <DadMark school={row.original.school} />,
      },
      {
        id: "size",
        header: "Undergrads",
        accessorFn: (d) => d.school.undergrad_size ?? -1,
        cell: ({ row }) => int(row.original.school.undergrad_size),
      },
      {
        id: "status",
        header: "Status",
        enableSorting: false,
        cell: ({ row }) => (
          <div onClick={(e) => e.stopPropagation()}>
            <StatusControl
              value={row.original.school.evaluation.status}
              onChange={(s) => void putEvaluation(row.original.school.id, { status: s })}
            />
          </div>
        ),
      },
      {
        id: "contact",
        header: "Last contact",
        accessorFn: (d) => d.lastInteraction ?? "",
        cell: ({ row }) =>
          row.original.lastInteraction ? (
            <span className="text-xs">{row.original.lastInteraction}</span>
          ) : (
            <span className="text-slate-300">—</span>
          ),
      },
    ],
    [putEvaluation],
  );

  const table = useReactTable({
    data: filtered,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div>
      <FilterBar
        q={q}
        setQ={setQ}
        trailing={
          <div>
            <div className="label mb-1">Saved filters</div>
            <div className="flex flex-wrap items-center gap-1">
              {filterPresets.map((p) => (
                <span
                  key={p.name}
                  className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-1.5 py-0.5 text-xs"
                >
                  <button
                    className="text-slate-700 hover:text-blue-600"
                    onClick={() => applyPreset(p)}
                  >
                    {p.name}
                  </button>
                  <button
                    className="text-slate-400 hover:text-rose-600"
                    aria-label={`Delete ${p.name}`}
                    onClick={() => deletePreset(p.name)}
                  >
                    ×
                  </button>
                </span>
              ))}
              {savingName === null ? (
                <button
                  className="rounded border border-slate-300 bg-white px-1.5 py-0.5 text-xs text-slate-600 hover:bg-slate-100"
                  onClick={() => setSavingName("")}
                >
                  + Save current
                </button>
              ) : (
                <span className="inline-flex items-center gap-1">
                  <input
                    autoFocus
                    className="input h-7 w-32 text-xs"
                    placeholder="name this set…"
                    value={savingName}
                    onChange={(e) => setSavingName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveCurrent();
                      if (e.key === "Escape") setSavingName(null);
                    }}
                  />
                  <button
                    className="rounded border border-slate-900 bg-slate-900 px-1.5 py-0.5 text-xs text-white"
                    onClick={saveCurrent}
                  >
                    Save
                  </button>
                  <button
                    className="rounded border border-slate-300 bg-white px-1.5 py-0.5 text-xs text-slate-600 hover:bg-slate-100"
                    onClick={() => setSavingName(null)}
                  >
                    ✕
                  </button>
                </span>
              )}
              {filterPresets.length === 0 && savingName === null && (
                <span className="text-xs text-slate-400">none yet</span>
              )}
            </div>
          </div>
        }
        groups={[
          { label: "Division", opts: DIVISIONS, set: fDiv, setSet: setFDiv },
          { label: "Distance", opts: [...PROXIMITIES], set: fProx, setSet: setFProx },
          {
            label: "Fit tier",
            opts: TIERS,
            set: fTier,
            setSet: setFTier,
            render: (t) => TIER_LABELS[t as Tier],
          },
          {
            label: "Status",
            opts: STATUSES,
            set: fStatus,
            setSet: setFStatus,
          },
          {
            label: "Scholarship $ (or more)",
            opts: [...DOLLARS],
            set: fSchol,
            setSet: setFSchol,
            directional: true,
            render: (d) => (d === "0" ? "none" : "$".repeat(Number(d))),
            titleFor: (d) => DOLLAR_LABEL[Number(d)],
          },
          {
            label: "Student ♥ (or more)",
            opts: [...LOVE],
            set: fLove,
            setSet: setFLove,
            directional: true,
            render: (v) => (v === "none" ? "n/a" : "♥".repeat(Number(v)) || "0"),
          },
          {
            label: "PT major",
            opts: [...PT_OPTS],
            set: fPt,
            setSet: setFPt,
            render: (v) => PT_LABEL[v],
          },
        ]}
      />
      <div className="mb-2 flex items-center gap-3 text-sm text-slate-600">
        <span>
          {filtered.length} of {schools.length} schools
        </span>
        <label className="flex items-center gap-1">
          <input type="checkbox" checked={hideCut} onChange={(e) => setHideCut(e.target.checked)} />
          hide cut
        </label>
        <label className="flex items-center gap-1">
          <input type="checkbox" checked={dadOnly} onChange={(e) => setDadOnly(e.target.checked)} />
          Dad's picks only
        </label>
        <label className="flex items-center gap-1">
          <input
            type="checkbox"
            checked={claudeOnly}
            onChange={(e) => setClaudeOnly(e.target.checked)}
          />
          Claude's 25 only
        </label>
      </div>

      {/* Mobile: card list */}
      <ul className="space-y-2 md:hidden">
        {table.getRowModel().rows.map((r) => {
          const d = r.original;
          const s = d.school;
          return (
            <li
              key={r.id}
              onClick={() => navigate(`/rowing/school/${s.id}`)}
              className="rounded-lg border border-slate-200 bg-white p-3"
            >
              <div className="flex items-start gap-2">
                <SchoolLogo school={s} size={32} />
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-900">{s.name}</div>
                  <div className="text-xs text-slate-500">
                    {s.division} · {orDash(s.conference)} · {orDash(s.state)} · {d.proximity}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
                    <TierBadge tier={d.tier} />
                    <ScholarshipDollars school={s} />
                    <StudentLoveHearts school={s} />
                    <PtMark school={s} />
                    {s.claude_pick && (
                      <span
                        className="rounded bg-violet-100 px-1 py-0.5 font-semibold text-violet-700"
                        title={s.claude_pick.note}
                      >
                        Claude #{s.claude_pick.rank}
                      </span>
                    )}
                    {s.evaluation.dadPick && (
                      <span className="font-bold text-emerald-600" title="Dad's pick">
                        ✓ Dad
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500">
                    cost {usdK(s.net_price)} · earnings {usdK(s.earnings_10yr)}
                  </div>
                </div>
              </div>
              <div className="mt-2 flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                <StatusControl
                  value={s.evaluation.status}
                  onChange={(st) => void putEvaluation(s.id, { status: st })}
                />
                <label className="flex items-center gap-1 text-xs text-slate-500">
                  rank
                  <RankCell
                    value={s.evaluation.katieRank}
                    onChange={(v) => void putEvaluation(s.id, { katieRank: v })}
                  />
                </label>
                {d.lastInteraction && (
                  <span className="ml-auto text-xs text-slate-400">contact {d.lastInteraction}</span>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {/* Desktop: table */}
      <div className="hidden max-h-[calc(100vh-13rem)] overflow-auto rounded-lg border border-slate-200 bg-white md:block">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 text-xs shadow-[0_1px_0_rgba(15,23,42,0.15)]">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => (
                  <th
                    key={h.id}
                    onClick={h.column.getToggleSortingHandler()}
                    className={`whitespace-nowrap bg-slate-100 px-2 py-2 ${
                      h.column.getCanSort() ? "cursor-pointer select-none hover:bg-slate-200" : ""
                    }`}
                  >
                    {flexRender(h.column.columnDef.header, h.getContext())}
                    {{ asc: " ▲", desc: " ▼" }[h.column.getIsSorted() as string] ?? ""}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((r) => (
              <tr
                key={r.id}
                onClick={() => navigate(`/rowing/school/${r.original.school.id}`)}
                className="cursor-pointer border-t border-slate-100 hover:bg-blue-50"
              >
                {r.getVisibleCells().map((c) => (
                  <td key={c.id} className="px-2 py-1.5 align-top">
                    {flexRender(c.column.columnDef.cell, c.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <FlaggedNote rows={filtered} />
    </div>
  );
}

function RankCell({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (v: number | null) => void;
}) {
  const [text, setText] = useState(value == null ? "" : String(value));
  useEffect(() => {
    setText(value == null ? "" : String(value));
  }, [value]);
  return (
    <input
      type="number"
      step="0.1"
      min="0"
      inputMode="decimal"
      placeholder="—"
      className="w-14 rounded border border-slate-300 px-1 py-0.5 text-right text-sm tabular-nums focus:border-slate-500 focus:outline-none"
      value={text}
      onChange={(e) => setText(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
      onBlur={() => {
        const t = text.trim();
        const n = t === "" ? null : Number(t);
        if (n === null || (Number.isFinite(n) && n >= 0)) onChange(n);
        else setText(value == null ? "" : String(value));
      }}
    />
  );
}

function PtMark({ school }: { school: School }) {
  if (school.pt_program === "has_pt")
    return (
      <span className="font-bold text-emerald-600" title="Clinical rehab/therapy doctorate (likely a DPT)">
        ✓
      </span>
    );
  if (school.pt_program === "related")
    return (
      <span className="font-bold text-amber-500" title="Pre-PT majors only (kinesiology / exercise science)">
        ✓
      </span>
    );
  return <span className="text-slate-300">·</span>;
}

function StackHeader({ top, bottom }: { top: string; bottom: string }) {
  return (
    <span className="block leading-tight">
      {top}
      <br />
      {bottom}
    </span>
  );
}

function DadMark({ school }: { school: School }) {
  if (!school.evaluation.dadPick) return null;
  return (
    <span className="font-bold text-emerald-600" title={school.evaluation.dadPickWhy || "Dad's pick"}>
      ✓
    </span>
  );
}

function ClaudePickMark({ school }: { school: School }) {
  const p = school.claude_pick;
  if (!p) return <span className="text-slate-300">·</span>;
  return (
    <span
      className="inline-block rounded bg-violet-100 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-violet-700"
      title={p.note}
    >
      #{p.rank}
    </span>
  );
}

function FlaggedNote({ rows }: { rows: DerivedSchool[] }) {
  const n = rows.filter((d) => d.school.data_flags.includes("no_cmax_data")).length;
  if (!n) return null;
  return (
    <p className="mt-2 text-xs text-slate-500">
      {n} of these have no cMax data — worth a manual deep-dive (recent results, boats fielded,
      coaching tenure) before keep/cut.
    </p>
  );
}

interface Group {
  label: string;
  opts: readonly string[];
  set: Set<string>;
  setSet: (s: Set<string>) => void;
  render?: (v: string) => string;
  titleFor?: (v: string) => string;
  /** opts are an ordered high→low scale: picking a numeric value also picks every
   *  stronger one ("3+"), but each chip can still be unchecked individually. */
  directional?: boolean;
}

function FilterBar({
  q,
  setQ,
  groups,
  trailing,
}: {
  q: string;
  setQ: (s: string) => void;
  groups: Group[];
  trailing?: React.ReactNode;
}) {
  const toggle = (g: Group, v: string) => {
    const next = new Set(g.set);
    const nums = g.opts.filter((o) => /^\d+$/.test(o)).map(Number);
    const isWeakest = /^\d+$/.test(v) && nums.length > 0 && Number(v) === Math.min(...nums);
    if (g.directional && /^\d+$/.test(v) && !isWeakest) {
      if (next.has(v)) {
        next.delete(v); // uncheck just this one
      } else {
        // pick this value and every stronger (numerically greater) one
        for (const o of g.opts) if (/^\d+$/.test(o) && Number(o) >= Number(v)) next.add(o);
      }
    } else {
      next.has(v) ? next.delete(v) : next.add(v);
    }
    g.setSet(next);
  };
  return (
    <div className="mb-3 flex flex-wrap items-start gap-x-5 gap-y-2 rounded-lg border border-slate-200 bg-white p-3">
      <input
        className="input w-56"
        placeholder="Search name / conference / state"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {groups.map((g) => (
        <div key={g.label}>
          <div className="label mb-1">{g.label}</div>
          <div className="flex flex-wrap gap-1">
            {g.opts.map((o) => (
              <button
                key={o}
                onClick={() => toggle(g, o)}
                title={g.titleFor ? g.titleFor(o) : undefined}
                className={`rounded border px-1.5 py-0.5 text-xs ${
                  g.set.has(o)
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-300 bg-white text-slate-600 hover:bg-slate-100"
                }`}
              >
                {g.render ? g.render(o) : o}
              </button>
            ))}
          </div>
        </div>
      ))}
      {trailing && <div className="ml-auto">{trailing}</div>}
    </div>
  );
}
